import {
  DestroyRef,
  Directive,
  effect,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  signal,
  WritableSignal,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormGroup, NgControl, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { BlockAbstract } from '@shared/models/step.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CourseContentService, ShortCodeValue } from '@app/courseContent/courseContent.service';
import { NgOnInitWriteBugAbstractComponent } from '@shared/abstract/ngOnInitWriteBug.abstract.component';

@Directive({})
export abstract class BlockAbstractComponent<
    T extends BlockAbstract,
    F extends { [K in keyof F]: AbstractControl<any, any> },
  >
  extends NgOnInitWriteBugAbstractComponent<T>
  implements OnInit, ControlValueAccessor
{
  @HostBinding('class') protected get hostBindingClass(): string {
    return Array.from(this.hostBindingClasses()).join(' ');
  }
  protected hostBindingClasses: WritableSignal<Set<string>> = signal(new Set(['block', 'mx-4', 'md:mx-0']));

  @Input() public isDisabled: boolean = false;
  @Output() public blockChanged: EventEmitter<T | null> = new EventEmitter<T | null>();

  @HostBinding('tabindex') public tabindex: number = 1;
  @HostListener('focus', ['$event.target']) public onFocus(target: T): void {
    this.onProgrammaticFocus(target);
  }

  public block: T | null = null;
  public blockId: number | null = null;
  public form: FormGroup<F> | null = null;

  private formChangesSubscription: Subscription | null = null;

  protected destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly DEBOUNCE_TIME_FORM_CHANGES: number = 50;

  constructor(
    @Optional() @Self() public override controlDir: NgControl,
    protected courseContentService: CourseContentService,
  ) {
    super(controlDir);

    effect(() => {
      this.onCourseContentFormSubmit(this.courseContentService.isMainFormSubmitted());
    });
  }

  protected abstract initForm(): void;
  protected getUpdatedBlock(): T | null {
    return this.block;
  }

  public plOnInit(): void {
    this.initComponent();
    if (this.controlDir.control) {
      this.controlDir.control.setValidators([this.validate.bind(this)]);
    }
  }

  protected initComponent(): void {
    this.initForm();
    this.watchFormChanges();
  }

  protected setBlockId(block: T | null): void {
    if (block) {
      this.blockId = block.id;
    } else {
      this.blockId = null;
    }
  }

  public plWriteValue(block: T): void {
    if (block !== undefined) {
      this.block = block;

      if (block === null) {
        this.setBlockId(null);
        this.patchFormWithEmptyValue();
      } else {
        this.setBlockId(block);
        this.hostBindingClasses.set(this.getCustomCssClassForBlock(block));
        this.patchFormFromBlock(block, this.getValueFromLocalStorage(block.program_data_code));
      }

      this.updateValueAndValidityAsync();
    }
  }

  /**
   * updateValueAndValidityAsync:
   *     We need to wait before updating as we are creating child component based on the block
   *     and the child component needs a cycle to get created and for his "validity" state to be reflected
   *
   * @param timeToWait
   *        for some blocks (via-survey for instance), one cycle is not enough, not sure why exactly to be honest
   * @protected
   */
  protected updateValueAndValidityAsync(timeToWait: number = 0): void {
    setTimeout(() => {
      if (this.controlDir.control) {
        this.controlDir.control.updateValueAndValidity({
          onlySelf: false,
          emitEvent: false,
        });
      }
    }, timeToWait);
  }

  protected abstract patchFormWithEmptyValue(): void;
  protected abstract patchFormFromBlock(block: T, valueFromLocalStorage?: ShortCodeValue): void;

  protected getCustomCssClassForBlock(block: T): Set<string> {
    const customCssClass: string[] | undefined = block.customCssClass;
    const currentClasses: Set<string> = this.hostBindingClasses();
    if (customCssClass && customCssClass.length > 0) {
      for (const cssClass of customCssClass) {
        currentClasses.add(cssClass);
      }
      this.hostBindingClasses.set(currentClasses);
    }

    return currentClasses;
  }

  private watchFormChanges(): void {
    if (this.form) {
      if (this.formChangesSubscription) {
        this.formChangesSubscription.unsubscribe();
      }

      this.formChangesSubscription = this.form.valueChanges
        .pipe(debounceTime(this.DEBOUNCE_TIME_FORM_CHANGES))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.emitBlockChange();
        });
    }
  }

  protected emitBlockChange(): void {
    const updatedBlock: T | null = this.getUpdatedBlock();
    this.blockChanged.emit(updatedBlock);
    this.onChangeCallback(updatedBlock);
  }

  protected validate(): ValidationErrors | null {
    return !this.form || this.form.valid
      ? null
      : {
          invalidForm: true,
        };
  }

  protected onCourseContentFormSubmit(isCourseContentFormSubmitted: boolean): void {
    if (isCourseContentFormSubmitted && this.form) {
      for (const control of Object.values(this.form.controls)) {
        control.markAsDirty();
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onProgrammaticFocus(target: T): void {
    // Do nothing by default, we only need to focus on blocks that are "interactive"
  }

  protected hideBlock(): void {
    const currentClasses: Set<string> = this.hostBindingClasses();
    currentClasses.delete('block');
    currentClasses.add('hidden');
    this.hostBindingClasses.set(currentClasses);
  }

  private getValueFromLocalStorage(shortCode: string | null): ShortCodeValue {
    if (shortCode) {
      return this.courseContentService.getShortCodeSavedInLocalStorage(shortCode);
    }

    return null;
  }
}
